import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import PropTypes from 'prop-types';
import path from 'path';
import React, { useEffect, useState } from 'react';
import { Popover, PopoverHeader, PopoverBody } from 'reactstrap';
import { createHashHistory } from 'history';
import entityComposition from '../../../common/classes/entityComposition';
import mapSettingsSelector from '../../selectors/mapSettingsSelector';
import { checkPermission, makeResource } from '../../../../../behaviors/src/lib/permissions';
import Icon from '../../../common/containers/icon';
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  popoverBody: {
    padding: '10px'
  },
  row: {
    marginBottom: '5px'
  },
  span: {
    marginLeft: '5px',
    fontStyle: 'normal'
  },
  icon: {
    opacity: '0.25'
  }
};
const hashHistory = createHashHistory();
const MapCallout = _ref => {
  let cabinetIdentifier = _ref.cabinetIdentifier,
    _ref$login = _ref.login,
    user = _ref$login.user,
    permissions = _ref$login.user.permissions,
    map = _ref.map,
    clusterItems = _ref.map.clusterItems,
    _ref$mapSettings = _ref.mapSettings,
    mapSettings = _ref$mapSettings === void 0 ? {} : _ref$mapSettings,
    info = _ref.notificationActions.info,
    _ref$selectedMarker = _ref.selectedMarker,
    selectedMarker = _ref$selectedMarker === void 0 ? null : _ref$selectedMarker,
    setSelectedMarker = _ref.setSelectedMarker,
    target = _ref.target;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    itemDetails = _useState2[0],
    setItemDetails = _useState2[1];
  const cabinetData = map[cabinetIdentifier] || {};
  const _cabinetData$list = cabinetData.list,
    list = _cabinetData$list === void 0 ? [] : _cabinetData$list;

  /**
   *  Navigate to formEditor when callout is clicked
   */
  const handleFormClick = formID => {
    if (!list.length || !list[0].features || !formID) return;
    setSelectedMarker(null);
    const selectedForm = list[0].features.find(f => f.properties.id === formID);
    const formTypeIdentifier = itemDetails.properties.formTypeIdentifier;
    const canViewDetail = checkPermission(makeResource('form', formTypeIdentifier), 'view_detail', permissions, user);
    if (canViewDetail !== true) {
      info({
        headerMessage: 'Permissions',
        message: "You do not have permission to view the details of ".concat(selectedForm.properties.formTypeIdentifier, "."),
        id: 'transition_while_resizing'
      });
      return;
    }
    const url = path.join('/cabinets', cabinetIdentifier, formTypeIdentifier, formID);
    hashHistory.push(url);
  };

  /**
   * Click handler to call handleFormClick
   */
  const handleClick = () => {
    if (selectedMarker !== null && selectedMarker !== void 0 && selectedMarker.id) {
      handleFormClick(selectedMarker.id);
    }
  };
  const ensurePropIsArray = value => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      if (value.trim().startsWith('[') && value.trim().endsWith(']')) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (e) {
          throw new Error('Failed to parse cluster properties');
        }
      }
      if (value.indexOf(',') !== -1) {
        return value.split(',').map(s => s.trim());
      }
      return [value];
    }
    return [];
  };
  useEffect(() => {
    if (clusterItems && clusterItems.features && clusterItems.features.length > 0) {
      setItemDetails(clusterItems.features[0]);
    }
  }, [clusterItems]);
  return selectedMarker && itemDetails && /*#__PURE__*/React.createElement(Popover, {
    "data-testid": "marker-popover",
    target: target,
    placement: "bottom",
    isOpen: Boolean(selectedMarker),
    onClick: handleFormClick ? handleClick : undefined,
    style: {
      width: "".concat(mapSettings.calloutSize.width, "px")
    }
  }, /*#__PURE__*/React.createElement(PopoverHeader, null, /*#__PURE__*/React.createElement("strong", null, selectedMarker.markerLabel)), /*#__PURE__*/React.createElement(PopoverBody, {
    style: styles.popoverBody
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.container
  }, /*#__PURE__*/React.createElement("div", null, ((_mapSettings$calloutL, _mapSettings$pinStyle) => {
    const formType = itemDetails.properties.formTypeIdentifier;
    const layout = ((_mapSettings$calloutL = mapSettings.calloutLayouts[formType]) === null || _mapSettings$calloutL === void 0 ? void 0 : _mapSettings$calloutL.rows) || [];
    const pinStyleTitleIndex = ((_mapSettings$pinStyle = mapSettings.pinStyles[formType]) === null || _mapSettings$pinStyle === void 0 ? void 0 : _mapSettings$pinStyle.titleIndex) || null;
    if (!layout.length) {
      return /*#__PURE__*/React.createElement("div", null, "No data available");
    }
    return layout.map(row => {
      if (row.index === pinStyleTitleIndex) {
        return null;
      }
      const key = "callout-row-".concat(selectedMarker.id, "-").concat(row.index);
      const rawValue = itemDetails.properties[row.index];
      const arrValue = ensurePropIsArray(rawValue);
      const displayValue = Array.isArray(arrValue) ? arrValue.join(row.separator || ', ') : arrValue;
      return /*#__PURE__*/React.createElement("div", {
        key: key,
        style: styles.row
      }, /*#__PURE__*/React.createElement("strong", null, "".concat(row.title, ":")), /*#__PURE__*/React.createElement("span", {
        style: styles.span
      }, displayValue));
    });
  })()), /*#__PURE__*/React.createElement(Icon, {
    glyph: "angle-right",
    style: styles.icon
  }))));
};
MapCallout.propTypes = {
  cabinetIdentifier: PropTypes.string.isRequired,
  selectedMarker: PropTypes.shape({
    id: PropTypes.string,
    markerLabel: PropTypes.string
  }),
  map: PropTypes.shape({
    clusterItems: PropTypes.shape({
      features: PropTypes.arrayOf(PropTypes.shape({
        properties: PropTypes.shape({
          formTypeIdentifier: PropTypes.string
        })
      }))
    })
  }).isRequired,
  mapSettings: PropTypes.shape({
    pinStyles: PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({
        title: PropTypes.string,
        titleIndex: PropTypes.string
      })
    }).isRequired,
    calloutSize: PropTypes.shape({
      width: PropTypes.number,
      singleHeight: PropTypes.number,
      clusterHeight: PropTypes.number
    }).isRequired,
    calloutLayouts: PropTypes.objectOf(PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({
        index: PropTypes.string.isRequired,
        style: PropTypes.string,
        title: PropTypes.string,
        titleStyle: PropTypes.string,
        separator: PropTypes.string
      })).isRequired
    })).isRequired
  }),
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }).isRequired,
  login: PropTypes.shape({
    user: PropTypes.shape({
      user_id: PropTypes.string,
      permissions: PropTypes.shape({
        formTypes: PropTypes.shape({})
      })
    })
  }).isRequired,
  setSelectedMarker: PropTypes.func.isRequired,
  target: PropTypes.shape({}).isRequired
};
export { MapCallout as DisconnectedMapCallout };
export default entityComposition(MapCallout, ['map', 'login'], {
  mapSettings: mapSettingsSelector
}, ['notification']);